import { useEffect, useRef } from "react";
import { useAudioPlayback, useRoomContext } from "@livekit/components-react";
import {
  ConnectionState,
  createAudioAnalyser,
  RoomEvent,
  Track,
  type LocalAudioTrack,
  type Participant,
  type RemoteAudioTrack,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
} from "livekit-client";
import {
  decodeReceipt,
  encodeReceipt,
  listenerState,
  lossBetween,
  micState,
  networkState,
  receiptKey,
  shouldSendReceipt,
  SILENT_LEVEL,
  VOICE_PATH_INTERVAL_MS,
  VOICE_RECEIPT_TOPIC,
  type ConnectionQualityValue,
  type ReceiptPlan,
  type ReceivedReceipt,
  type SentReceipt,
  type VoicePath,
} from "../lib/voicePath";
import {
  MAX_OUTAGES_PER_UPLOAD,
  OutageTracker,
  STATS_READ_TIMEOUT_MS,
  TalkingWindow,
  endedFlag,
  settleWithin,
  type RemoteSample,
} from "../lib/outageTracker";
import { videoService } from "../api/video.api";
import {
  InPlaceRecoveryWatch,
  StretchTally,
  type CallQualityTally,
  describeBrowser,
  FIRST_QUALITY_REPORT_MS,
  QUALITY_REPORT_INTERVAL_MS,
} from "../lib/callQuality";
import { useCallVolumeStore } from "../store/callVolumeStore";
import { useCallSessionStore } from "../store/callSessionStore";
import { useVoicePathStore } from "../store/voicePathStore";
import { useCallDiagnosticsStore } from "../store/callDiagnosticsStore";
import { installFaults, type RoomLike } from "../lib/callFaults";
import { createNotHeardRunner, type NotHeardRoomLike } from "../lib/notHeardRunner";
import { micIntent } from "../lib/notHeard";
import { restartLocalMic } from "../hooks/useLocalMediaRecovery";
import { mixContext } from "../hooks/useAudioPlaybackUnlock";
import {
  AudibilityTracker,
  AudibleRecoveryLadder,
  type AudibleAction,
  type AudibleSpeakerSample,
} from "../lib/audibility";
import { fallbackPlaybackLevel, gainPathLevel } from "../lib/playbackLevel";
import { requestGatedRejoin } from "../lib/rejoinGate";
import { emitCallActivity } from "../lib/callActivity";
import { isCallMonitor } from "../lib/callMonitor";

const LEVEL_INTERVAL_MS = 250;
/** Outage stats are read this often, so an outage is timed to about a second. */
const OUTAGE_INTERVAL_MS = 1000;
/** Resubscribe (listener recovery step 2): how long the mic stays unsubscribed. */
const RESUBSCRIBE_GAP_MS = 300;

/** Remote mics this listener should be able to hear: published, unmuted, a person. */
function audibleCandidates(participants: Iterable<RemoteParticipant>) {
  const out: Array<{ participant: RemoteParticipant; pub: RemoteTrackPublication }> = [];
  for (const participant of participants) {
    if (participant.isAgent || isCallMonitor(participant)) continue;
    const pub = participant.getTrackPublication(Track.Source.Microphone);
    if (pub && !pub.isMuted) out.push({ participant, pub });
  }
  return out;
}

/**
 * Measures whether this participant's voice is getting through, and tells
 * each speaker whether their voice arrives here. Renders nothing.
 *
 * Mounted once per call, next to `CallAudioRenderer`, so the notes keep
 * flowing whichever view is on screen (full call, floating widget, guest
 * page). Every 3 s it reads stats locally (one read on the outgoing mic, one
 * per incoming mic -- no network). A ~40-byte note goes out only when what
 * it says changes, or every 15 s, and only to participants whose mic is on
 * (see lib/voicePath.ts).
 *
 * With `report`, it also adds up the call's quality (lib/callQuality.ts) and
 * sends it for the super admin's call report 15 s in, then once a minute,
 * and on leaving. Every second it also reads the packet counters that decide
 * outages (lib/outageTracker.ts); those events ride along on each report, and
 * the "missing" list in the notes to speakers comes from the same verdict.
 *
 * The same 1 s samples drive "You're not being heard" (lib/notHeardRunner.ts):
 * the speaker's own warning and recovery, and the "can't be heard right now"
 * marks on other speakers' tiles.
 *
 * As a listener it also checks that each talking speaker is *audible* here
 * (#2223 AC44–AC46, lib/audibility.ts): LiveKit's server-side `isSpeaking`
 * says who talks, an analyser on each track's gain node in the mix context
 * (lib/playbackLevel.ts) says what actually plays. A "couldn't hear" stretch
 * is recorded on the timeline (`not_audible`), uploaded with the outages,
 * named in the receipts to that speaker, and after 5 s runs the listener's
 * own recovery: resume audio → resubscribe → rejoin (lib/rejoinGate.ts keeps
 * it from rejoining right after the not-heard ladder did, or looping).
 *
 * The final report says `ended: "left"` when the user chose to go (Leave,
 * closing the tab, reloading) and says nothing when the app tore the
 * connection down itself to recover or the server removed us.
 * `isDeliberateLeave` tells the monitor which; it defaults to the shell
 * call session's leave-intent flag.
 */
export function VoicePathMonitor({
  report,
  isDeliberateLeave,
}: {
  report?: { roomId: string; token: string };
  isDeliberateLeave?: () => boolean;
}) {
  const room = useRoomContext();
  const { canPlayAudio } = useAudioPlayback(room);
  const volume = useCallVolumeStore((s) => s.volume);
  const playingRef = useRef(false);
  playingRef.current = canPlayAudio && volume > 0;
  const canPlayRef = useRef(canPlayAudio);
  canPlayRef.current = canPlayAudio;
  // Latest room and token: the token is re-minted on a rejoin.
  const reportRef = useRef(report);
  reportRef.current = report;
  const deliberateRef = useRef(isDeliberateLeave);
  deliberateRef.current = isDeliberateLeave;

  // Staging-only test faults (#2224): exist only when the server allows
  // diagnostics AND the URL has ?fault=1. Reactive to the flag, separate from
  // the monitor below so a late flag never restarts measurement. Leaving the
  // call (unmount) clears every fault and puts the mic back.
  const diagnosticsOn = useCallDiagnosticsStore((s) => s.enabled);
  const faultsRef = useRef<ReturnType<typeof installFaults>>(null);
  useEffect(() => {
    const faults = installFaults(room as unknown as RoomLike, diagnosticsOn, window.location.search);
    faultsRef.current = faults;
    return () => {
      faults?.dispose();
      if (faultsRef.current === faults) faultsRef.current = null;
    };
  }, [room, diagnosticsOn]);

  useEffect(() => {
    const { setPath, setLevel, reset } = useVoicePathStore.getState();
    const receipts = new Map<string, ReceivedReceipt>();
    const onData = (payload: Uint8Array, participant?: Participant, _kind?: unknown, topic?: string) => {
      if (topic !== VOICE_RECEIPT_TOPIC || !participant) return;
      const receipt = decodeReceipt(payload);
      if (receipt) receipts.set(participant.identity, { receipt, at: Date.now() });
    };
    room.on(RoomEvent.DataReceived, onData);

    // ── The call report: added up every sample, sent once a minute ─────────
    // One tally per participant sid: a full rejoin keeps this monitor but
    // starts a new participation (see StretchTally).
    const stretches = new StretchTally(room.localParticipant.sid);
    let lastTick = Date.now();
    // In-place recoveries only: a restart gets a new sid and is a drop already.
    const recoveries = new InPlaceRecoveryWatch();
    const onSignalReconnecting = () => recoveries.signalReconnecting();
    const onReconnecting = () => recoveries.reconnecting();
    const onReconnected = () => {
      if (recoveries.reconnected()) stretches.tally.reconnected();
    };
    room.on(RoomEvent.SignalReconnecting, onSignalReconnecting);
    room.on(RoomEvent.Reconnecting, onReconnecting);
    room.on(RoomEvent.Reconnected, onReconnected);
    const outages = new OutageTracker();
    // ── Listener side: is each talking speaker audible here? (#2223) ──────
    const audibility = new AudibilityTracker();
    const audibleLadder = new AudibleRecoveryLadder();
    // Per speaker, over the current second: readings, readings while the
    // server said speaking, highest played level (undefined = not on the
    // gain path, measure another way).
    const heard = new Map<string, { readings: number; speaking: number; max: number | undefined }>();
    const resubscribeTimers = new Set<number>();
    const recordAudible = (transitions: ReturnType<AudibilityTracker["observe"]>) => {
      for (const t of transitions) {
        emitCallActivity("not_audible", { speaker: t.speaker, state: t.state });
        console.info(`[weco-audible] ${t.state === "start" ? "can't hear" : "hears again"} ${t.speaker}`);
      }
    };
    const runAudible = (action: AudibleAction | null, at: number) => {
      if (!action) return;
      const record = (step: string, result: "started" | "failed") =>
        emitCallActivity("recovery", { trigger: "not_audible", step, result });
      console.info(`[weco-audible] recovery ${action}`);
      if (action === "resumeAudio") {
        // The existing unlock path does the same on visibility/reconnect; a
        // resume without a gesture works whenever the context ran before.
        const context = mixContext(room);
        if (context && (context.state as string) !== "running" && context.state !== "closed") {
          void context.resume().catch(() => {});
        }
        void room.startAudio().catch(() => {});
        record("resume_audio", "started");
      } else if (action === "resubscribe") {
        let any = false;
        for (const { participant, pub } of audibleCandidates(room.remoteParticipants.values())) {
          if (!audibility.notAudible().includes(participant.identity)) continue;
          any = true;
          pub.setSubscribed(false);
          const timer = window.setTimeout(() => {
            resubscribeTimers.delete(timer);
            pub.setSubscribed(true);
          }, RESUBSCRIBE_GAP_MS);
          resubscribeTimers.add(timer);
        }
        record("resubscribe", any ? "started" : "failed");
      } else {
        const outcome = requestGatedRejoin(at);
        if (outcome !== "started") console.info(`[weco-audible] rejoin not started (${outcome})`);
        record("rejoin", outcome === "started" ? "started" : "failed");
      }
    };
    const notHeard = createNotHeardRunner(room as unknown as NotHeardRoomLike, {
      restartMic: () => restartLocalMic(room),
      diagnostics: () => useCallDiagnosticsStore.getState().enabled,
    });
    // The sid is read while connected (it may be gone once we have
    // disconnected), so a report names the stretch its tally belongs to.
    const sendReport = (
      keepalive: boolean,
      ended?: "left",
      stretch: { sid: string; tally: CallQualityTally } = { sid: stretches.stretchSid, tally: stretches.tally },
    ) => {
      const target = reportRef.current;
      if (!target || stretch.tally.observedMs === 0) return;
      // A deliberate end leaves nothing open: close what is still running.
      if (ended) {
        outages.finish(Date.now());
        recordAudible(audibility.finish(Date.now()));
      }
      void videoService.reportCallQuality(
        target.roomId,
        target.token,
        // Outages are not split per stretch: the server keys them per call
        // and keeps each on the stretch it started in.
        stretch.tally.report(stretch.sid || room.localParticipant.sid, describeBrowser(navigator.userAgent), {
          outages: [...outages.snapshot(), ...audibility.snapshot()]
            .sort((a, b) => b.started_at - a.started_at)
            .slice(0, MAX_OUTAGES_PER_UPLOAD),
          ended,
        }),
        { keepalive },
      );
    };
    const firstReportTimer = window.setTimeout(() => sendReport(false), FIRST_QUALITY_REPORT_MS);
    const reportTimer = window.setInterval(() => sendReport(false), QUALITY_REPORT_INTERVAL_MS);
    // A closed tab never unmounts; keepalive lets this last one through.
    // Closing or reloading the tab is the user's choice -- unless the page is
    // only going into the back/forward cache and may come back.
    const onPageHide = (e: PageTransitionEvent) => sendReport(true, endedFlag(!e.persisted));
    window.addEventListener("pagehide", onPageHide);

    // ── Mic level, several times a second ──────────────────────────────────
    let analyser: ReturnType<typeof createAudioAnalyser> | null = null;
    let analysed: MediaStreamTrack | null = null;
    let level: number | null = null;
    // Every level reading since the last outage sample: "talking" is judged
    // over the same second as the byte rate it is compared with.
    const talking = new TalkingWindow();
    let lastSound = Date.now();
    const micTrack = () =>
      room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track as LocalAudioTrack | undefined;
    const sampleLevel = () => {
      const track = micTrack();
      const stream = track?.mediaStreamTrack ?? null;
      if (stream !== analysed) {
        // A new device, or a restarted track: measure the one now in use.
        void analyser?.cleanup().catch(() => {});
        analyser = null;
        analysed = stream;
        lastSound = Date.now();
        if (track) {
          try {
            analyser = createAudioAnalyser(track, { minDecibels: -90, maxDecibels: -10, smoothingTimeConstant: 0.5 });
          } catch {
            analyser = null;
          }
        }
      }
      if (!analyser || analyser.analyser.context.state !== "running" || track?.isMuted) {
        level = null;
        lastSound = Date.now();
      } else {
        level = analyser.calculateVolume();
        if (level > SILENT_LEVEL) lastSound = Date.now();
      }
      talking.add(level);
      setLevel(level);

      // What plays for each remote mic, and whether the server says they talk.
      for (const { participant, pub } of audibleCandidates(room.remoteParticipants.values())) {
        const w = heard.get(participant.identity) ?? { readings: 0, speaking: 0, max: undefined };
        w.readings += 1;
        if (participant.isSpeaking) w.speaking += 1;
        const track = pub.track as RemoteAudioTrack | undefined;
        const played = track ? gainPathLevel(track) : 0;
        if (played !== undefined) w.max = Math.max(w.max ?? 0, played);
        heard.set(participant.identity, w);
      }
    };
    const levelTimer = window.setInterval(sampleLevel, LEVEL_INTERVAL_MS);

    // ── Outages: packet counters, every second ─────────────────────────────
    let sampling = false;
    // The user's mute intent, kept across samples with no mic publication.
    let wantsToBeHeard = false;
    const sampleOutages = async () => {
      if (sampling) return;
      sampling = true;
      try {
        const now = Date.now();
        const connected = room.state === ConnectionState.Connected;
        const local = room.localParticipant;
        // A full rejoin gave us a new participation: send the old stretch's
        // last numbers under its own sid, then count afresh.
        const finished = stretches.roll(local.sid);
        if (finished) sendReport(false, undefined, finished);
        const micPub = local.getTrackPublication(Track.Source.Microphone);
        const track = micTrack();
        const muted = !track || !!micPub?.isMuted;
        let packetsSent: number | null = null;
        let bytesSent: number | null = null;
        let remotes: RemoteSample[] = [];
        if (connected) {
          if (track && !muted) {
            const stats = await settleWithin(track.getSenderStats(), STATS_READ_TIMEOUT_MS, undefined);
            packetsSent = stats?.packetsSent ?? null;
            bytesSent = stats?.bytesSent ?? null;
            // fakeNothingSent changes only what is measured here, never the audio.
            const faults = faultsRef.current;
            if (faults) ({ packetsSent, bytesSent } = faults.controller.outboundSample({ packetsSent, bytesSent }));
          }
          remotes = await Promise.all(
            Array.from(room.remoteParticipants.values()).map(async (p): Promise<RemoteSample> => {
              const pub = p.getTrackPublication(Track.Source.Microphone);
              const expected = !!pub && !pub.isMuted;
              const packetsReceived = expected
                ? await settleWithin(receivedPackets(pub?.track as RemoteTrack | undefined), STATS_READ_TIMEOUT_MS, null)
                : null;
              return { identity: p.identity, expected, packetsReceived };
            }),
          );
        }
        outages.observe({
          at: now,
          connected,
          local: { published: !!track, muted, packetsSent, bytesSent, talking: talking.take() },
          remotes,
        });
        // Listener side: who talked this second, and did anything audible play?
        const volumeOn = useCallVolumeStore.getState().volume > 0;
        const speakers: AudibleSpeakerSample[] = connected
          ? await Promise.all(
              audibleCandidates(room.remoteParticipants.values()).map(async ({ participant, pub }) => {
                const w = heard.get(participant.identity);
                // Speaking at every reading of the second (and now): the end
                // of a sentence must not read as "talking but silent".
                const talkingNow = !!w && w.readings >= 2 && w.speaking === w.readings && participant.isSpeaking;
                let level: number | null = null;
                // The user's own volume at 0 is a choice, not a fault (`playing` says so).
                if (talkingNow && volumeOn) {
                  if (w?.max !== undefined) level = w.max;
                  else {
                    const track = pub.track as RemoteAudioTrack | undefined;
                    level = track ? await settleWithin(fallbackPlaybackLevel(track), STATS_READ_TIMEOUT_MS, null) : 0;
                  }
                }
                return { identity: participant.identity, talking: talkingNow, level };
              }),
            )
          : [];
        heard.clear();
        // The stretch's own `not_audible` end event says when it was over;
        // no separate "ok" is recorded, since a speaker who simply stopped
        // talking ends it too and that proves nothing.
        recordAudible(audibility.observe({ at: now, connected, speakers }));
        runAudible(audibleLadder.update({ at: now, since: audibility.oldestOpenSince() }), now);

        wantsToBeHeard = micIntent(wantsToBeHeard, micPub);
        notHeard.observe({
          at: now,
          connected,
          // Intent, not "a track exists": a drop or rejoin unpublishes the mic,
          // which must not read as the user muting (that would clear the
          // warning and restart the ladder). No track = an "unknown" verdict.
          unmuted: wantsToBeHeard,
          outbound: outages.outboundVerdict(),
          missingSince: outages.missingSince(),
        });
      } finally {
        sampling = false;
      }
    };
    const outageTimer = window.setInterval(() => void sampleOutages(), OUTAGE_INTERVAL_MS);

    // ── Everything else, every few seconds ─────────────────────────────────
    let prevSender: { sent: number; lost: number } | null = null;
    let lastSent: SentReceipt | null = null;
    let unmutedSince: number | null = null;
    let busy = false;

    const tick = async () => {
      if (busy) return;
      busy = true;
      try {
        const now = Date.now();
        const local = room.localParticipant;
        const micPub = local.getTrackPublication(Track.Source.Microphone);
        const track = micTrack();
        const sending = !!track && !micPub?.isMuted;
        if (!sending) unmutedSince = null;
        else if (unmutedSince === null) unmutedSince = now;

        // 2. Network: what the server reports about our outgoing audio.
        let packetsSentDelta: number | null = null;
        let lossPct: number | null = null;
        let rttMs: number | null = null;
        if (track && sending) {
          const stats = await settleWithin(track.getSenderStats(), STATS_READ_TIMEOUT_MS, undefined);
          if (stats?.packetsSent !== undefined) {
            const next = { sent: stats.packetsSent, lost: stats.packetsLost ?? 0 };
            packetsSentDelta = prevSender ? next.sent - prevSender.sent : null;
            lossPct = lossBetween(prevSender, next);
            prevSender = next;
          }
          if (stats?.roundTripTime !== undefined) rttMs = Math.round(stats.roundTripTime * 1000);
        } else {
          prevSender = null;
        }

        // 3. Note out: which speakers' audio is not arriving here. Sent
        // only to speakers, and only when it changes or a heartbeat is due.
        const missing = new Set(outages.missingSpeakers());
        const inaudible = new Set(audibility.notAudible());
        const plan: ReceiptPlan = {
          missing: [],
          arriving: [],
          playing: playingRef.current,
          destinations: [],
          inaudible: [],
        };
        for (const participant of room.remoteParticipants.values()) {
          const pub = participant.getTrackPublication(Track.Source.Microphone);
          if (!pub || pub.isMuted) continue;
          plan.destinations.push(participant.identity);
          // Whether audio is arriving is the outage tracker's verdict (no
          // audio for 2 s while expected), not a guess made once per tick.
          (missing.has(participant.identity) ? plan.missing : plan.arriving).push(participant.identity);
          if (inaudible.has(participant.identity)) plan.inaudible.push(participant.identity);
        }
        if (room.state === ConnectionState.Connected && shouldSendReceipt(plan, lastSent, now)) {
          lastSent = { key: receiptKey(plan), at: now };
          void local
            .publishData(
              encodeReceipt({
                v: 2,
                missing: plan.missing,
                playing: plan.playing,
                // Omitted when empty: the note stays ~40 bytes in the normal case.
                ...(plan.inaudible.length ? { inaudible: plan.inaudible } : {}),
              }),
              {
              // Rare now, so reliable: a lost change would otherwise wait
              // for the next heartbeat.
              reliable: true,
              topic: VOICE_RECEIPT_TOPIC,
              destinationIdentities: plan.destinations,
              },
            )
            .catch(() => {
              lastSent = null;
            });
        }

        const path: VoicePath = {
          mic: micState({
            published: !!track,
            muted: !!micPub?.isMuted,
            level,
            quietForMs: now - lastSound,
          }),
          micLevel: level,
          network: networkState({
            connected: room.state === ConnectionState.Connected,
            micSending: sending,
            packetsSentDelta,
            lossPct,
            rttMs,
            quality: local.connectionQuality as ConnectionQualityValue,
          }),
          lossPct,
          rttMs,
          quality: local.connectionQuality as ConnectionQualityValue,
          listeners: Array.from(room.remoteParticipants.values())
            // Agents (the meeting twin) listen but send no receipts.
            .filter((p) => !p.isAgent)
            .map((p) => ({
              identity: p.identity,
              name: p.name || p.identity,
              state: listenerState(local.identity, receipts.get(p.identity), { now, unmutedSince }),
            })),
        };
        setPath(path);
        stretches.tally.add({
          elapsedMs: now - lastTick,
          path,
          notArriving: plan.missing,
          notAudible: [...inaudible],
          connected: room.state === ConnectionState.Connected,
          canPlayAudio: canPlayRef.current,
        });
        lastTick = now;
      } finally {
        busy = false;
      }
    };
    void tick();
    const tickTimer = window.setInterval(() => void tick(), VOICE_PATH_INTERVAL_MS);

    return () => {
      // Unmounting is a deliberate leave only when the caller says so; a
      // rejoin or a server-side removal also ends up here, and those are drops.
      const deliberate = deliberateRef.current
        ? deliberateRef.current()
        : useCallSessionStore.getState().leaveIntent;
      sendReport(true, endedFlag(deliberate));
      room.off(RoomEvent.DataReceived, onData);
      room.off(RoomEvent.SignalReconnecting, onSignalReconnecting);
      room.off(RoomEvent.Reconnecting, onReconnecting);
      room.off(RoomEvent.Reconnected, onReconnected);
      window.removeEventListener("pagehide", onPageHide);
      window.clearTimeout(firstReportTimer);
      window.clearInterval(reportTimer);
      window.clearInterval(levelTimer);
      window.clearInterval(outageTimer);
      window.clearInterval(tickTimer);
      void analyser?.cleanup().catch(() => {});
      for (const timer of resubscribeTimers) window.clearTimeout(timer);
      notHeard.dispose();
      reset();
    };
  }, [room]);

  return null;
}

/** Cumulative RTP packets received for one incoming mic, or null when there are no stats at all. */
async function receivedPackets(track: RemoteTrack | undefined): Promise<number | null> {
  // LiveKit's own audio receiver stats leave packetsReceived out (checked
  // against livekit-client 2.19), so ask the receiver directly.
  const receiver = track?.receiver;
  if (!receiver?.getStats) return null;
  try {
    let packets: number | null = null;
    (await receiver.getStats()).forEach((v) => {
      if (v.type === "inbound-rtp" && typeof v.packetsReceived === "number") packets = v.packetsReceived;
    });
    return packets;
  } catch {
    return null;
  }
}
